import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ShoppingCart, Verified, FlashOn, SupportAgent, Security } from '@mui/icons-material';
import SEO from '../../common/SEO';
import bgImg from '../../../assets/images/windows_os_banner.png';
import serverImg from '../../../assets/images/windows_server_2025.png';

const serviceCategories = [
  {
    category: 'Windows Operating Systems',
    emoji: '💻',
    description: 'Original license keys for Windows operating systems with lifetime activation and automatic security updates.',
    gradient: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
    items: [
      {
        name: 'Windows 10 Pro Retail Key',
        description: 'Genuine Microsoft retail license key with permanent validity. Transferable to a new computer and linkable directly to your Microsoft account.',
        features: ['100% Genuine Retail Key', 'Lifetime Validity', 'Account Linkable', 'Transferable License'],
      },
      {
        name: 'Windows 10 Pro OEM Key',
        description: 'Cost-effective OEM product key binding to a single PC motherboard. Permanent activation for custom system builds or upgrades.',
        features: ['Official OEM Bind Key', 'Motherboard Locked', 'Most Cost-Effective', 'Full Update Support'],
      },
      {
        name: 'Windows 11 Pro Retail Key',
        description: "Get Microsoft's latest OS with snap groups, advanced security, Auto HDR, and a streamlined interface. Transferable retail license.",
        features: ['Sleek Snap Layouts', 'TPM 2.0 Compliant', 'Account Linkable', 'Lifetime Key'],
      },
      {
        name: 'Windows 11 Pro OEM Key',
        description: 'Standard OEM key for Windows 11 Pro, tied to your motherboard hardware. Full activation at the lowest price.',
        features: ['Hardware Locked ID', 'Modern Fluent UI', 'Full Windows Update', 'Instant Delivery'],
      }
    ]
  },
  {
    category: 'Microsoft Office Suites',
    emoji: '📊',
    description: 'Classic desktop applications for productivity at home, school, and work without monthly subscription costs.',
    gradient: 'linear-gradient(135deg, #1E3A8A 0%, #0e6ada 100%)',
    items: [
      {
        name: 'MS Office 2024 Home & Business',
        description: 'One-time purchase license for PC or macOS. Includes Word, Excel, PowerPoint, Outlook, and OneNote. Binds to your Microsoft account.',
        features: ['PC & Mac Compatible', 'Account Bind Key', 'Word, Excel, PPT, Outlook', 'No Monthly Fees'],
      },
      {
        name: 'MS Office 2021 Professional Plus',
        description: 'Full suite for Windows PCs. Includes classic desktop apps: Word, Excel, PowerPoint, Outlook, Publisher, and Access.',
        features: ['Full Office Suite', 'Windows 10/11 Only', 'Includes Access & Publisher', 'Permanent Activation'],
      },
      {
        name: 'MS Office 2024 Professional Plus',
        description: 'The latest classic desktop applications with enhanced capabilities, formatting options, and database management.',
        features: ['Latest Office Suite', 'Lifetime Activation', 'One-Time Payment', 'Fast Setup Guide'],
      }
    ]
  },
  {
    category: 'Windows Server Solutions',
    emoji: '🗄️',
    description: 'Enterprise-grade operating systems providing multi-layer security and hybrid cloud environments for business use.',
    gradient: 'linear-gradient(135deg, #0a1628 0%, #0e3a7a 100%)',
    items: [
      {
        name: 'Windows Server 2025 Standard Key',
        description: 'Deploy state-of-the-art server features, nested virtualization, active directory security, and Azure hybrid cloud integration.',
        features: ['Standard Server License', 'Multi-Core Coverage', 'Azure Arc Integrated', 'Enterprise Grade Security'],
      }
    ]
  }
];

const trustPoints = [
  { icon: <Verified />, title: '100% Genuine', desc: 'Sourced directly from official Microsoft channels' },
  { icon: <FlashOn />, title: 'Instant Delivery', desc: 'Keys sent to your inbox within minutes of payment' },
  { icon: <Security />, title: 'Secure Checkout', desc: 'SSL encrypted payments with GST invoice' },
  { icon: <SupportAgent />, title: '24/7 Support', desc: 'Expert help for installation and activation' },
];

const OurServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box>
      <SEO
        title="Our Digital Services & Licenses Range | AR Infotech"
        description="Explore the full catalog of genuine Microsoft product keys offered by AR Infotech. Including Windows 10/11 Pro, Microsoft Office 2024, and Windows Server 2025 keys."
      />

      {/* ─── HERO with Blurred BG Image ─── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 320, md: 440 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Blurred Background */}
        <Box
          component="img"
          src={bgImg}
          alt=""
          sx={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            filter: 'blur(7px) brightness(0.35)',
            transform: 'scale(1.1)',
          }}
        />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'linear-gradient(135deg, rgba(10,22,60,0.8) 0%, rgba(14,80,180,0.6) 60%, rgba(0,150,200,0.3) 100%)',
          }}
        />

        {/* Hero Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', py: { xs: 7, md: 10 } }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Chip
              label="⚡ Instant E-Delivery • Lifetime Activation"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(0,210,196,0.15)',
                border: '1px solid rgba(0,210,196,0.5)',
                color: '#00D2C4', fontWeight: 700, fontSize: { xs: '0.75rem', md: '0.9rem' }, px: 1.5,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#FFF', fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4.2rem' },
                lineHeight: 1.1, mb: 2,
                textShadow: '0 2px 30px rgba(0,0,0,0.4)'
              }}
            >
              Premium Software{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #00D2C4, #87CEEB)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Licenses
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.85)', fontWeight: 400,
                maxWidth: 700, mx: 'auto', lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Get genuine and lifetime Microsoft operating systems, server configurations, and office suites — with instant e-delivery and direct activation.
            </Typography>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href="/shop"
                startIcon={<ShoppingCart />}
                sx={{
                  px: 4, py: 1.5, fontWeight: 700, borderRadius: '50px',
                  background: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
                  boxShadow: '0 6px 24px rgba(14,106,218,0.5)',
                }}
              >
                Shop All Licenses
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="/contact"
                sx={{
                  px: 4, py: 1.5, fontWeight: 700, borderRadius: '50px',
                  color: '#FFF', borderColor: 'rgba(255,255,255,0.6)', borderWidth: 2,
                  backdropFilter: 'blur(8px)',
                  '&:hover': { borderColor: '#FFF', borderWidth: 2 }
                }}
              >
                Get Expert Advice
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* ─── TRUST STRIP ─── */}
      <Box sx={{ background: 'linear-gradient(135deg, #0e6ada 0%, #0a3a8a 100%)', py: { xs: 3, md: 4 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {trustPoints.map((t, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#FFF', py: 1 }}>
                  <Box sx={{ color: '#00D2C4', '& svg': { fontSize: '1.6rem' } }}>{t.icon}</Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFF', fontSize: '0.85rem' }}>{t.title}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>{t.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── SERVICE CATEGORIES ─── */}
      <Box sx={{ background: 'linear-gradient(160deg, #EBF6FF 0%, #F5FAFF 60%, #FFFFFF 100%)', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {serviceCategories.map((cat, idx) => (
            <Box key={idx} sx={{ mb: { xs: 8, md: 12 } }}>
              {/* Category Header */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 5,
                    p: { xs: 3, md: 4 }, borderRadius: 3,
                    background: cat.gradient,
                    boxShadow: '0 12px 40px rgba(14,106,218,0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Decorative circle */}
                  <Box
                    sx={{
                      position: 'absolute', right: -30, top: -30,
                      width: 160, height: 160, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                    }}
                  />
                  <Typography sx={{ fontSize: { xs: '2.5rem', md: '3rem' } }}>{cat.emoji}</Typography>
                  <Box>
                    <Typography variant="h4" sx={{ color: '#FFF', fontWeight: 800, mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {cat.category}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      {cat.description}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>

              {/* Service Cards */}
              <Grid container spacing={3}>
                {cat.items.map((srv, srvIdx) => (
                  <Grid item xs={12} md={cat.items.length === 1 ? 8 : 6} key={srvIdx}
                    sx={cat.items.length === 1 ? { mx: 'auto' } : {}}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: srvIdx * 0.1 }}
                      style={{ height: '100%' }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          boxShadow: '0 4px 24px rgba(14,106,218,0.08)',
                          transition: 'all 0.3s ease',
                          border: '1px solid rgba(14,106,218,0.1)',
                          background: '#FFFFFF',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 20px 50px rgba(14,106,218,0.18)',
                            borderColor: 'rgba(14,106,218,0.3)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, mb: 2, color: '#0e3a7a', fontSize: { xs: '1.1rem', md: '1.35rem' } }}
                          >
                            {srv.name}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7, minHeight: { md: 80 } }}>
                            {srv.description}
                          </Typography>

                          <Divider sx={{ mb: 3, borderColor: 'rgba(14,106,218,0.12)' }} />

                          <Typography
                            variant="overline"
                            sx={{ fontWeight: 700, color: '#0e6ada', letterSpacing: 1.5, display: 'block', mb: 2 }}
                          >
                            Key Specifications
                          </Typography>

                          <Grid container spacing={1}>
                            {srv.features.map((feat, fIdx) => (
                              <Grid item xs={12} sm={6} key={fIdx}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CheckCircleIcon sx={{ fontSize: 16, color: '#0e6ada', flexShrink: 0 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{feat}</Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>

                          <Button
                            variant="outlined"
                            size="small"
                            href="/shop"
                            sx={{
                              mt: 3, borderRadius: '50px',
                              borderColor: '#0e6ada', color: '#0e6ada', fontWeight: 600,
                              '&:hover': { backgroundColor: '#0e6ada', color: '#FFF' }
                            }}
                          >
                            View in Shop →
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Container>
      </Box>

      {/* ─── CTA SECTION with blurred server image ─── */}
      <Box sx={{ position: 'relative', overflow: 'hidden', py: { xs: 8, md: 12 } }}>
        {/* Blurred BG */}
        <Box
          component="img"
          src={serverImg}
          alt=""
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            filter: 'blur(8px) brightness(0.3)',
            transform: 'scale(1.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'linear-gradient(135deg, rgba(10,22,60,0.85) 0%, rgba(14,80,180,0.7) 100%)',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFF', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              Looking for a specific license?
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', mb: 4, fontWeight: 400 }}>
              Browse our entire inventory of Windows systems, Office tools, and Server editions with guaranteed activation.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href="/shop"
                startIcon={<ShoppingCart />}
                sx={{
                  px: 5, py: 1.5, fontWeight: 700, borderRadius: '50px',
                  background: 'linear-gradient(135deg, #00D2C4 0%, #0e6ada 100%)',
                  boxShadow: '0 6px 24px rgba(0,210,196,0.4)',
                  '&:hover': { boxShadow: '0 8px 32px rgba(0,210,196,0.6)' }
                }}
              >
                Shop the Collection
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="/contact"
                sx={{
                  px: 5, py: 1.5, fontWeight: 700, borderRadius: '50px',
                  color: '#FFF', borderColor: 'rgba(255,255,255,0.6)', borderWidth: 2,
                  backdropFilter: 'blur(8px)',
                  '&:hover': { borderColor: '#FFF', borderWidth: 2 }
                }}
              >
                Contact Us
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default OurServices;
